import type { RootState } from "~/app/store";
import instance from "~/utils/i18n";
import { XML_TRANSLATION_SHEETS } from "~/features/configurator/common/useXmlConfiguratorTranslations";
import {
  type CarriageKmatSummaryData,
  type GaSelectionData,
  type RailKmatSummaryData,
  SummaryDataProvider,
} from "~/features/configurator/Shaft/SummaryDataProvider";
import type { ConfiguratorPageDefinition } from "~/features/configurator/ConfiguratorPageDefinition";
import { sendEvent } from "~/app/analytics/ga/ga";
import { getConfiguratorFamilyFromProductCode } from "~/domains/configurator/types/ConfiguratorType";
import type { WithT } from "i18next";
import { prepareSummaryDataName, prepareSummaryDataValue } from "~/features/configurator/common/v2/gaHelper";

export const makeSummaryDataProvider = (state: RootState, language = "en") => {
  const configuratorType = state.linearConfigurator.type!;
  const productCode = state.linearConfigurator.productCode!;

  const configuratorFamily = getConfiguratorFamilyFromProductCode(productCode);
  const originalT = instance.getFixedT(language, XML_TRANSLATION_SHEETS[configuratorType].sheetName);
  const t = ((key: string, options?: object) =>
    originalT(key, { context: configuratorFamily, ...options })) as WithT["t"];
  return new SummaryDataProvider(state.linearConfigurator, t);
};

export function convertToSnakeCase(name: string) {
  return name.trim().replace(/\s+/g, "_").toLowerCase();
}

export function makeSendStepCompletedEvent(configuratorDefinition: ConfiguratorPageDefinition) {
  return (stepName: string, selectionData: GaSelectionData) => {
    sendEvent({
      event: "linearConfigInteraction",
      variant: configuratorDefinition.gaConfiguratorName,
      step: stepName,
      push_context: `${configuratorDefinition.gaConfiguratorName}_step_completed`,
      selection_data: selectionData,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } as any);
  };
}

export function transformRailDataForGA(data: RailKmatSummaryData[]) {
  const configuredGuidewayData = {} as Record<string, Record<string, string>>;

  const railAmount = data[0]?.rail?.length ?? 0;
  if (!railAmount) {
    console.warn("Invalid rail amount");
    return configuredGuidewayData;
  }

  for (let railIndex = 0; railIndex < railAmount; railIndex++) {
    const guidewayData = {} as Record<string, string>;

    data.forEach((item) => {
      const name = prepareSummaryDataName(item);
      const value = prepareSummaryDataValue(item.rail[railIndex]);
      guidewayData[name] = value;
    });

    configuredGuidewayData[`guideway_set_${railIndex + 1}`] = guidewayData;
  }

  return configuredGuidewayData;
}

export function transformCarriageDataForGA(data: CarriageKmatSummaryData[], railAmount: number) {
  const configuredCarriageData = {} as Record<string, Record<string, Record<string, string>>>;

  if (!railAmount) {
    console.warn("Invalid rail amount");
    return configuredCarriageData;
  }

  const carriageAmount = (data[0]?.rail?.length ?? 0) / railAmount;
  if (!carriageAmount || !Number.isInteger(carriageAmount)) {
    console.warn("Invalid carriage amount");
    return configuredCarriageData;
  }

  for (let railIndex = 0; railIndex < railAmount; railIndex++) {
    const guidewayData = {} as Record<string, Record<string, string>>;

    for (let carriageIndex = 0; carriageIndex < carriageAmount; carriageIndex++) {
      const carriageData = {} as Record<string, string>;

      data.forEach((item) => {
        const name = prepareSummaryDataName(item);
        const value = prepareSummaryDataValue(item.rail[railIndex].carriage[carriageIndex]);
        carriageData[name] = value;
      });

      guidewayData[`carriage_set_${carriageIndex + 1}`] = carriageData;
    }

    configuredCarriageData[`guideway_set_${railIndex + 1}`] = guidewayData;
  }

  return configuredCarriageData;
}
